import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

/**
 * Custom TipTap extension that enforces a maximum nesting depth for lists.
 * This prevents bullet lists, ordered lists, and task lists from exceeding
 * the configured maximum depth (default: 3 levels).
 */
export interface MaxNestingOptions {
  maxDepth: number;
}

const LIST_TYPES = new Set(['bulletList', 'orderedList', 'taskList']);

export const MaxNestingExtension = Extension.create<MaxNestingOptions>({
  name: 'maxNesting',

  addOptions() {
    return {
      maxDepth: 3,
    };
  },

  addProseMirrorPlugins() {
    const maxDepth = this.options.maxDepth;

    return [
      new Plugin({
        key: new PluginKey('maxNesting'),
        filterTransaction(transaction) {
          if (!transaction.docChanged) return true;

          const maxFound = getMaxListDepth(transaction.doc);
          return maxFound <= maxDepth;
        },
      }),
    ];
  },
});

/**
 * Recursively walk the document tree and find the maximum list nesting depth.
 * A top-level list has depth 1, a list inside a list item has depth 2, etc.
 */
export function getMaxListDepth(doc: ProseMirrorNode): number {
  let maxDepth = 0;

  function walk(node: ProseMirrorNode, currentListDepth: number) {
    const isListNode = LIST_TYPES.has(node.type.name);
    const newDepth = isListNode ? currentListDepth + 1 : currentListDepth;

    if (newDepth > maxDepth) {
      maxDepth = newDepth;
    }

    node.forEach(child => {
      walk(child, newDepth);
    });
  }

  walk(doc, 0);
  return maxDepth;
}

export default MaxNestingExtension;
